import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { Starfield } from "@/components/Starfield";
import { Globe2, Sparkles, LogOut, Volume2, VolumeX, Languages, MessageCircle } from "lucide-react";
import { useState } from "react";
import { isSoundOn, setSoundOn } from "@/lib/sound";

const navItems = [
  { to: "/chat", key: "nav.chat", icon: MessageCircle, testid: "nav-chat" },
  { to: "/map", key: "nav.map", icon: Globe2, testid: "nav-map" },
  { to: "/creatures", key: "nav.creatures", icon: Sparkles, testid: "nav-creatures" },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useT();
  const navigate = useNavigate();
  const [soundOn, setSound] = useState(isSoundOn());

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setSound(next);
  };

  return (
    <div className="relative min-h-screen bg-[#050814] text-[#FDFBF7]">
      <Starfield />
      <div className="relative z-10 flex min-h-screen flex-col">
        <header className="sticky top-0 z-30 border-b border-[#D4AF37]/15 bg-[#050814]/70 backdrop-blur-2xl">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6">
            <button
              onClick={() => navigate("/chat")}
              data-testid="logo-home"
              className="flex items-center gap-2"
            >
              <Sparkles className="h-6 w-6 text-[#D4AF37]" />
              <span className="font-serif text-xl font-semibold tracking-tight gold-text">
                {t("app.name")}
              </span>
            </button>

            <nav className="hidden items-center gap-1 md:flex">
              {navItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  data-testid={it.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors ${
                      isActive
                        ? "bg-[#D4AF37]/15 text-[#D4AF37]"
                        : "text-[#A3B1C6] hover:text-[#D4AF37]"
                    }`
                  }
                >
                  <it.icon className="h-4 w-4" />
                  {t(it.key)}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setLang(lang === "zh" ? "en" : "zh")}
                data-testid="lang-toggle"
                className="flex items-center gap-1 text-sm text-[#A3B1C6] transition-colors hover:text-[#D4AF37]"
                title="Language"
              >
                <Languages className="h-5 w-5" />
                {lang === "zh" ? "EN" : "中"}
              </button>
              <button
                onClick={toggleSound}
                data-testid="sound-toggle"
                className="text-[#A3B1C6] transition-colors hover:text-[#D4AF37]"
                title={soundOn ? t("sound.on") : t("sound.off")}
              >
                {soundOn ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
              </button>
              <button
                onClick={() => navigate("/profile")}
                data-testid="nav-profile"
                className="flex items-center gap-2"
              >
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="h-9 w-9 rounded-full border border-[#D4AF37]/50 object-cover"
                  />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-[#0A0F1D] text-sm">
                    {user?.name?.[0] || "旅"}
                  </div>
                )}
              </button>
              <button
                onClick={logout}
                data-testid="logout-button"
                className={`text-[#A3B1C6] transition-colors hover:text-[#D4AF37] ${user?.is_guest ? "hidden" : ""}`}
                title="登出"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* mobile nav */}
          <nav className="flex items-center justify-around border-t border-[#D4AF37]/10 px-2 py-2 md:hidden">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={`m-${it.testid}`}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 rounded-lg px-2 py-1 text-[10px] ${
                    isActive ? "text-[#D4AF37]" : "text-[#A3B1C6]"
                  }`
                }
              >
                <it.icon className="h-5 w-5" />
                {t(it.key)}
              </NavLink>
            ))}
          </nav>
        </header>

        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 md:px-6 md:py-10">
          {children}
        </main>
      </div>
    </div>
  );
};
